import { useEffect, useMemo, useRef, useState } from 'react';
import { InventorySKUListing } from '@views/StockBySKU/components/types';
import { CubeIcon } from '@heroicons/react/24/outline';
import { Button } from '@tailwindComponents/Button';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { SkuMenuOption } from './SkuActionsMenu';
import { SkuTableRow } from './SkuTableRow';
import { skuCode } from './skuHelpers';

export interface SkuTableProps {
  skus: InventorySKUListing[];
  isLoading: boolean;
  isWarehouseUser: boolean;
  onOpenSku: (sku: InventorySKUListing) => void;
  buildMenuOptions: (sku: InventorySKUListing) => SkuMenuOption[];
  onArchiveSku: (sku: InventorySKUListing) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  canManageSkus: boolean;
  onUploadClick: () => void;
  onAddClick: () => void;
}

export function SkuTable({
  skus,
  isLoading,
  isWarehouseUser,
  onOpenSku,
  buildMenuOptions,
  onArchiveSku,
  hasActiveFilters,
  onClearFilters,
  canManageSkus,
  onUploadClick,
  onAddClick,
}: SkuTableProps) {
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const [copiedSkuId, setCopiedSkuId] = useState<string | null>(null);
  const copyResetTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(copyResetTimeout.current), []);

  const visibleIds = useMemo(
    () => skus.map((sku) => sku.inventorySkuId),
    [skus]
  );

  useEffect(() => {
    const visible = new Set(visibleIds);
    setExpandedRowIds((previous) => {
      const next = new Set([...previous].filter((id) => visible.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [visibleIds]);

  const handleToggleExpand = (inventorySkuId: string) => {
    setExpandedRowIds((previous) => {
      const next = new Set(previous);
      if (next.has(inventorySkuId)) {
        next.delete(inventorySkuId);
      } else {
        next.add(inventorySkuId);
      }
      return next;
    });
  };

  const handleCopy = (sku: InventorySKUListing) => {
    navigator.clipboard?.writeText(skuCode(sku));
    setCopiedSkuId(sku.inventorySkuId);
    clearTimeout(copyResetTimeout.current);
    copyResetTimeout.current = setTimeout(() => setCopiedSkuId(null), 1500);
  };

  const headerCell = 'px-4 py-3 text-xs font-medium text-gray-500';

  return (
    <div className='overflow-x-auto rounded-lg border border-gray-200 bg-white'>
      <table className='w-full min-w-[860px] table-auto'>
        <thead className='bg-gray-50'>
          <tr>
            <th scope='col' className='w-10 px-2'>
              <span className='sr-only'>Expand</span>
            </th>
            <th scope='col' className={`${headerCell} text-left`}>
              Name
            </th>
            <th scope='col' className={`${headerCell} text-left`}>
              SKU ID
            </th>
            <th scope='col' className={`${headerCell} text-center`}>
              Dimensions
            </th>
            <th scope='col' className={`${headerCell} text-center`}>
              Weight
            </th>
            <th scope='col' className={`${headerCell} text-center`}>
              Status
            </th>
            <th scope='col' className={`${headerCell} text-right`}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-200'>
          {isLoading && (
            <tr>
              <td colSpan={7} className='py-12 text-center'>
                <div className='flex flex-col items-center gap-3'>
                  <FloxSpinner size='md' />
                  <span className='text-sm text-gray-500'>Loading SKUs...</span>
                </div>
              </td>
            </tr>
          )}
          {!isLoading && skus.length === 0 && (
            <tr>
              <td colSpan={7} className='py-12 text-center'>
                <div className='flex flex-col items-center gap-3'>
                  <CubeIcon className='h-12 w-12 text-gray-300' />
                  <p className='text-sm font-medium text-gray-900'>
                    No SKUs found
                  </p>
                  {hasActiveFilters ? (
                    <>
                      <p className='text-sm text-gray-500'>
                        No SKUs match the current filters.
                      </p>
                      <Button
                        intent='tertiary'
                        size='sm'
                        label='Clear filters'
                        onClick={onClearFilters}
                      />
                    </>
                  ) : (
                    <>
                      <p className='text-sm text-gray-500'>
                        Add your first SKU to get started.
                      </p>
                      {canManageSkus && (
                        <div className='flex items-center gap-2'>
                          <Button
                            intent='primary'
                            size='sm'
                            label='Add your first SKU'
                            onClick={onAddClick}
                          />
                          <Button
                            intent='tertiary'
                            size='sm'
                            label='or upload in bulk'
                            onClick={onUploadClick}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </td>
            </tr>
          )}
          {!isLoading &&
            skus.map((sku) => (
              <SkuTableRow
                key={sku.inventorySkuId}
                sku={sku}
                isExpanded={expandedRowIds.has(sku.inventorySkuId)}
                onToggleExpand={handleToggleExpand}
                onOpen={onOpenSku}
                isWarehouseUser={isWarehouseUser}
                menuOptions={buildMenuOptions(sku)}
                onArchive={onArchiveSku}
                isCopied={copiedSkuId === sku.inventorySkuId}
                onCopy={handleCopy}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
}
